import { useMutation, useQuery } from "@tanstack/react-query";
import { Alert, App, Button, Form, Input, Modal, Skeleton, Space } from "antd";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch } from "../api/client";
import { useServerNotifications } from "../api/notifications";
import type { CongregationArchiveRow, HostDetails, NotificationsResponse } from "../api/types";

interface HostFormValues {
  speaker_host_name: string;
  speaker_host_email: string;
  speaker_host_phone: string;
  speaker_host_address: string;
}

/**
 * Who puts the visiting speaker up for one week — and the way to take that back.
 *
 * Two screens offer it, the congregation's calendar and the talk assignments,
 * so the dialog lives here rather than being written twice.
 *
 * It is mounted per week (`{hostOf && <HostDialog … />}`). That gives the form
 * a store of its own each time, which is what lets `initialValues` fill it:
 * writing into a shared store before the modal mounts is what once made every
 * edit dialog in this project open empty — see test/editorModal.test.tsx. For
 * the same reason the form is rendered only once the details have arrived:
 * mounting it earlier would freeze the empty values it was mounted with.
 */
export function HostDialog({
  week,
  onClose,
  onChanged,
}: {
  week: CongregationArchiveRow;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message } = App.useApp();
  const [form] = Form.useForm<HostFormValues>();

  const showError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
    [notify, message],
  );

  const done = useCallback(
    (data: NotificationsResponse) => {
      notify(data.notifications);
      onChanged();
      onClose();
    },
    [notify, onChanged, onClose],
  );

  // The list row carries the host's name and nothing else — email, telephone
  // and address are read here, or the editor would offer to overwrite what it
  // never showed.
  const host = useQuery({
    queryKey: ["local_assignation", "host", week.week_begin],
    queryFn: () => apiFetch<HostDetails>(`/local_assignation/events/${week.week_begin}/host`),
  });

  const save = useMutation({
    mutationFn: (values: HostFormValues) =>
      apiFetch<NotificationsResponse>(`/local_assignation/events/${week.week_begin}/host`, {
        method: "PUT",
        body: values,
      }),
    onSuccess: done,
    onError: showError,
  });

  const remove = useMutation({
    mutationFn: () =>
      apiFetch<NotificationsResponse>(`/local_assignation/events/${week.week_begin}/host`, {
        method: "DELETE",
      }),
    onSuccess: done,
    onError: showError,
  });

  return (
    <Modal
      open
      title={[t("local_assignation.host"), week.week_label].filter(Boolean).join(" · ")}
      onCancel={onClose}
      okText={t("form.update")}
      okButtonProps={{ loading: save.isPending, disabled: host.data === undefined }}
      onOk={() => form.submit()}
      footer={(_, { OkBtn, CancelBtn }) => (
        <Space>
          {/* Only a week that has one can lose it; the server answers
              "not_selected" for the others. */}
          {week.hospitality_status === "established" && (
            <Button danger loading={remove.isPending} onClick={() => remove.mutate()}>
              {t("local_assignation.host_cancel")}
            </Button>
          )}
          <CancelBtn />
          <OkBtn />
        </Space>
      )}
    >
      {host.isPending && <Skeleton active paragraph={{ rows: 4 }} />}

      {host.error != null && (
        <Alert
          type="error"
          showIcon
          title={host.error instanceof Error ? host.error.message : String(host.error)}
        />
      )}

      {host.data && (
        <Form<HostFormValues>
          form={form}
          layout="vertical"
          initialValues={{
            speaker_host_name: host.data.speaker_host_name ?? "",
            speaker_host_email: host.data.speaker_host_email ?? "",
            speaker_host_phone: host.data.speaker_host_phone ?? "",
            speaker_host_address: host.data.speaker_host_address ?? "",
          }}
          onFinish={(values) => save.mutate(values)}
        >
          {/* Name and address are what the server insists on; it answers
              "host_incomplete" without them. */}
          <Form.Item
            name="speaker_host_name"
            label={t("activerecord.attributes.local_assignation.speaker_host_name")}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="speaker_host_email"
            label={t("activerecord.attributes.local_assignation.speaker_host_email")}
            rules={[{ required: true }, { type: "email" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="speaker_host_phone"
            label={t("activerecord.attributes.local_assignation.speaker_host_phone")}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="speaker_host_address"
            label={t("activerecord.attributes.local_assignation.speaker_host_address")}
          >
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}
